import { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { BoxProps, SpacingValue, BoxBorderRadius } from './Box.types';

/**
 * Spacing map based on production theme
 */
const spacingMap: Record<number, string> = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  7: '28px',
  8: '32px',
  9: '36px',
  10: '40px',
};

/**
 * Border radius map
 */
const borderRadiusMap: Record<BoxBorderRadius, string> = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
};

/**
 * Helper to resolve spacing value
 */
const resolveSpacing = (value: SpacingValue | undefined): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'number') {
    return spacingMap[value] || `${value * 4}px`;
  }
  return value;
};

/**
 * Styled Box container
 */
const StyledBox = styled.div<{
  $pt?: string;
  $pr?: string;
  $pb?: string;
  $pl?: string;
  $mt?: string;
  $mr?: string;
  $mb?: string;
  $ml?: string;
  $variant?: 'default' | 'accented';
  $accentColor?: string;
  $borderRadius?: string;
}>`
  padding-top: ${({ $pt }) => $pt};
  padding-right: ${({ $pr }) => $pr};
  padding-bottom: ${({ $pb }) => $pb};
  padding-left: ${({ $pl }) => $pl};
  margin-top: ${({ $mt }) => $mt};
  margin-right: ${({ $mr }) => $mr};
  margin-bottom: ${({ $mb }) => $mb};
  margin-left: ${({ $ml }) => $ml};
  border-radius: ${({ $borderRadius }) => $borderRadius};

  ${({ $variant, $accentColor }) =>
    $variant === 'accented' &&
    $accentColor &&
    css`
      background: linear-gradient(135deg, ${$accentColor}14 0%, rgba(0, 0, 0, 0.02) 100%);
      border: 1px solid ${$accentColor}26;
    `}
`;

/**
 * Box component - A basic container with spacing and visual styling props
 *
 * @example
 * ```tsx
 * // Padding on all sides
 * <Box p={4}>
 *   <Content />
 * </Box>
 *
 * // Horizontal and vertical padding
 * <Box px={4} py={2}>
 *   <Content />
 * </Box>
 *
 * // Individual spacing values
 * <Box pt={4} pb={2} ml={3}>
 *   <Content />
 * </Box>
 *
 * // As a section element
 * <Box as="section" p={4} mb={3}>
 *   <Content />
 * </Box>
 *
 * // Accented variant with custom color (for branded sections)
 * <Box variant="accented" accentColor="#6161FF" p={5} borderRadius="md">
 *   <BrandedContent />
 * </Box>
 * ```
 */
export const Box = forwardRef<HTMLElement, BoxProps>(
  (
    {
      children,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      variant = 'default',
      accentColor,
      borderRadius = 'none',
      as: Component = 'div',
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
      ...rest
    },
    ref
  ) => {
    // Resolve padding values with priority: individual > axis > all
    const resolvedPt = resolveSpacing(pt ?? py ?? p);
    const resolvedPr = resolveSpacing(pr ?? px ?? p);
    const resolvedPb = resolveSpacing(pb ?? py ?? p);
    const resolvedPl = resolveSpacing(pl ?? px ?? p);

    // Resolve margin values with priority: individual > axis > all
    const resolvedMt = resolveSpacing(mt ?? my ?? m);
    const resolvedMr = resolveSpacing(mr ?? mx ?? m);
    const resolvedMb = resolveSpacing(mb ?? my ?? m);
    const resolvedMl = resolveSpacing(ml ?? mx ?? m);

    // Resolve border radius
    const resolvedBorderRadius = borderRadiusMap[borderRadius];

    return (
      <StyledBox
        as={Component}
        ref={ref as React.Ref<HTMLDivElement>}
        className={className}
        $pt={resolvedPt}
        $pr={resolvedPr}
        $pb={resolvedPb}
        $pl={resolvedPl}
        $mt={resolvedMt}
        $mr={resolvedMr}
        $mb={resolvedMb}
        $ml={resolvedMl}
        $variant={variant}
        $accentColor={accentColor}
        $borderRadius={resolvedBorderRadius}
        data-testid={dataTestId || testId}
        data-id={dataId}
        {...rest}
      >
        {children}
      </StyledBox>
    );
  }
);

Box.displayName = 'Box';

export default Box;
